/**
 * AI B2B Leads, the Search tab (super admin only): describe who you want, search, watch it run, keep what you like.
 *
 * A search runs in the background on the server (a hidden browser reads leads.cm; you never see it), so this page starts it,
 * polls its progress, and shows results as they arrive. It survives leaving the page: the newest unfinished search is picked
 * up again on return. Adding a result to a campaign only creates a contact; nothing is called until you start the campaign.
 */
import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, Play, Save, Search, Sparkles, Square, UserPlus, X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { AddToCampaignDialog } from "./AddToCampaignDialog";
import { ColumnPicker } from "./ColumnPicker";
import { useColumnChoice } from "./columns";
import { ExportButton } from "./ExportButton";
import { LeadFilterForm } from "./LeadFilterForm";
import { LeadsPager } from "./LeadsPager";
import { LeadsTable } from "./LeadsTable";
import {
  emptyValues, filtersToValues, isActiveJob, valuesToFilters,
  type FormValues, type ParsedPrompt, type ProspectLead, type SearchJobView, type SearchResponse, type SearchStatus, type Vocabulary,
} from "./types";

const POLL_MS = 2000;
const RESULTS_PER_PAGE = 50;

export function LeadsCmSearchPanel() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const vocabQuery = useQuery<Vocabulary>({ queryKey: ["/api/prospect-leads/filter-vocabulary"] });
  const fields = vocabQuery.data?.fields ?? [];
  const columns = useColumnChoice(vocabQuery.data);

  const statusQuery = useQuery<SearchStatus>({
    queryKey: ["prospect-leads", "search-status"],
    queryFn: async () => (await apiRequest("GET", "/api/prospect-leads/search/status")).json(),
    enabled: !!vocabQuery.data,
    refetchInterval: 15_000,
    staleTime: 0,
  });
  const status = statusQuery.data;

  // ---- the request ----
  const [prompt, setPrompt] = useState("");
  const [parsed, setParsed] = useState<ParsedPrompt | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const [maxResults, setMaxResults] = useState("");
  const formValues = useMemo(() => ({ ...emptyValues(fields), ...values }), [fields, values]);
  const filters = useMemo(() => valuesToFilters(formValues, fields), [formValues, fields]);
  const hasCountry = typeof filters.country === "string" && filters.country !== "";
  const hasNarrowing = Object.keys(filters).some((k) => k !== "country");

  // `run` = "Suggest and search": fill the form from the description and, when it was read completely, start the search too.
  const suggest = useMutation({
    mutationFn: async (_run: boolean) => (await (await apiRequest("POST", "/api/prospect-leads/parse-prompt", { prompt })).json()) as ParsedPrompt,
    onSuccess: (r, run) => {
      setParsed(r); setValues(filtersToValues(r.filters, fields)); setMaxResults(String(r.maxResults));
      if (!run) return;
      // Only a complete reading runs by itself: a country, something to narrow by, and nothing changed, dropped or unsupported.
      // Anything else is shown for the person to check, because a search takes over a minute and uses the shared leads.cm account.
      const complete = (r.warnings?.length ?? 0) === 0 && (r.notApplied?.length ?? 0) === 0 && typeof r.filters.country === "string" && Object.keys(r.filters).some((k) => k !== "country");
      if (complete && status?.available && !active) start.mutate({ filters: r.filters, maxResults: r.maxResults });
      else toast({ title: t("prospectLeads.find.checkFirst"), description: t("prospectLeads.find.checkFirstWhy") });
    },
    onError: (e: any) => toast({ title: t("prospectLeads.find.failed"), description: e?.message, variant: "destructive" }),
  });

  // ---- the running search ----
  const [jobId, setJobId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<string, ProspectLead>>(new Map());
  const [showCampaign, setShowCampaign] = useState(false);

  const recentQuery = useQuery<{ data: SearchJobView[] }>({
    queryKey: ["prospect-leads", "search-recent"],
    queryFn: async () => (await apiRequest("GET", "/api/prospect-leads/search")).json(),
    enabled: !!vocabQuery.data,
    staleTime: 0,
  });
  // Coming back to the page mid-search: pick the unfinished one up again.
  useEffect(() => {
    if (jobId || !recentQuery.data?.data) return;
    const active = recentQuery.data.data.find((j) => isActiveJob(j));
    if (active) setJobId(active.id);
  }, [jobId, recentQuery.data]);

  const jobQuery = useQuery<SearchResponse>({
    queryKey: ["prospect-leads", "search", jobId, page],
    queryFn: async () => (await apiRequest("GET", `/api/prospect-leads/search/${jobId}?page=${page}&pageSize=${RESULTS_PER_PAGE}`)).json(),
    enabled: !!jobId,
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchInterval: (q) => (isActiveJob(q.state.data?.job) ? POLL_MS : false),
  });
  const job = jobQuery.data?.job;
  const rows = jobQuery.data?.results.data ?? [];
  const pagination = jobQuery.data?.results.pagination;
  const active = isActiveJob(job);

  // When a search finishes, the saved-leads views may have changed too.
  useEffect(() => {
    if (job && !active) { queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search-recent"] }); queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search-status"] }); }
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useMutation({
    mutationFn: async (body: { filters: unknown; maxResults?: number; datasetFrom?: number }) => {
      const res = await apiRequest("POST", "/api/prospect-leads/search", body);
      return (await res.json()) as { jobId: string; warnings: string[] };
    },
    onSuccess: (r) => {
      setJobId(r.jobId); setPage(1); setSelected(new Map());
      queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search-recent"] });
      if (r.warnings?.length) toast({ title: t("prospectLeads.search.warnings"), description: r.warnings.join(" ") });
    },
    onError: (e: any) => toast({ title: t("prospectLeads.search.startFailed"), description: e?.message, variant: "destructive" }),
  });

  const stop = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/prospect-leads/search/${jobId}/cancel`)).json(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search", jobId] }),
    onError: (e: any) => toast({ title: t("prospectLeads.search.stopFailed"), description: e?.message, variant: "destructive" }),
  });

  const keep = useMutation({
    mutationFn: async () => (await (await apiRequest("POST", "/api/prospect-leads/search/keep", { ids: [...selected.keys()] })).json()) as { kept: number },
    onSuccess: (r) => {
      toast({ title: t("prospectLeads.search.saved", { count: r.kept }) });
      setSelected(new Map());
      queryClient.invalidateQueries({ queryKey: ["prospect-leads"] });
    },
    onError: (e: any) => toast({ title: t("prospectLeads.search.saveFailed"), description: e?.message, variant: "destructive" }),
  });

  // ---- selection ----
  const maxSelection = vocabQuery.data?.limits.maxSelection ?? 1000;
  const withinCap = (n: number) => { if (n <= maxSelection) return true; toast({ title: t("prospectLeads.leads.selectionCap", { max: maxSelection }), variant: "destructive" }); return false; };
  const toggleRow = (lead: ProspectLead) => {
    const next = new Map(selected);
    if (next.has(lead.id)) next.delete(lead.id); else { if (!withinCap(next.size + 1)) return; next.set(lead.id, lead); }
    setSelected(next);
  };
  const togglePage = () => {
    const next = new Map(selected);
    const all = rows.length > 0 && rows.every((r) => next.has(r.id));
    if (all) rows.forEach((r) => next.delete(r.id)); else { rows.forEach((r) => next.set(r.id, r)); if (!withinCap(next.size)) return; }
    setSelected(next);
  };
  const selectedList = useMemo(() => [...selected.values()], [selected]);
  const unsavedSelected = selectedList.filter((l) => l.status === "result").length;

  // ---- what to say about the search ----
  const canSearch = !!status?.available && hasCountry && hasNarrowing && !active && !start.isPending;
  const startSearch = () => {
    const n = Number.parseInt(maxResults, 10);
    start.mutate({ filters, ...(Number.isFinite(n) && n > 0 ? { maxResults: n } : {}) });
  };
  const progressText = (j: SearchJobView): string => {
    if (j.status === "queued") return t("prospectLeads.search.phase.queued");
    if (j.phase === "loading" && j.currentDataset) return j.datasetsTotal ? t("prospectLeads.search.phase.loading", { current: j.currentDataset, total: j.datasetsTotal }) : t("prospectLeads.search.phase.loadingUnknown", { current: j.currentDataset });
    if (j.phase === "scanning" && j.currentDataset) return t("prospectLeads.search.phase.scanning", { current: j.currentDataset });
    return t("prospectLeads.search.phase.starting");
  };
  const doneText = (j: SearchJobView): string => {
    if (j.stopReason === "target_reached") return t("prospectLeads.search.done.target", { n: j.maxResults ?? j.results });
    if (j.stopReason === "datasets_exhausted") return t("prospectLeads.search.done.exhausted", { country: j.country });
    if (j.stopReason === "dataset_cap") return t("prospectLeads.search.done.cap", { n: j.datasetsDone });
    return t("prospectLeads.search.done.other");
  };
  const statusBadge = (s: SearchJobView["status"]) => <Badge variant={s === "failed" ? "destructive" : s === "completed" ? "default" : "secondary"} data-testid="job-status">{t(`prospectLeads.search.status.${s}`)}</Badge>;

  const scanMore = () => job?.nextDataset && start.mutate({ filters: job.filters, maxResults: job.maxResults ?? undefined, datasetFrom: job.nextDataset });
  // ---- LeadPro Ask AI: describe the leads in English; the search runs in one request and the job is already finished ----
  const isAsk = status?.mode === "leadpro";
  const [question, setQuestion] = useState("");
  const [askAnswer, setAskAnswer] = useState<{ explanation: string | null; truncated: boolean } | null>(null);
  const ask = useMutation({
    mutationFn: async () => {
      const n = Number.parseInt(maxResults, 10);
      const res = await apiRequest("POST", "/api/prospect-leads/ask", { question, ...(Number.isFinite(n) && n > 0 ? { maxResults: n } : {}) });
      return (await res.json()) as { jobId: string; explanation: string | null; truncated: boolean };
    },
    onSuccess: (r) => {
      setAskAnswer({ explanation: r.explanation, truncated: r.truncated });
      setJobId(r.jobId); setPage(1); setSelected(new Map());
      queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search-recent"] });
    },
    onError: (e: any) => toast({ title: t("prospectLeads.search.startFailed"), description: e?.message, variant: "destructive" }),
  });


  return (
    <div className="space-y-6" data-testid="leadscm-search-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Search className="h-5 w-5" />{t("prospectLeads.search.title")}</CardTitle>
          <CardDescription>{t("prospectLeads.search.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status && !status.available && (
            <Alert variant="destructive" data-testid="search-unavailable">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t("prospectLeads.search.unavailable")}</AlertTitle>
              <AlertDescription>
                {status.message}
                {status.cooldownUntil && <span className="ml-1">{t("prospectLeads.search.pausedUntil", { time: new Date(status.cooldownUntil).toLocaleTimeString() })}</span>}
              </AlertDescription>
            </Alert>
          )}

          {isAsk && (
            <div className="space-y-3" data-testid="leadpro-ask">
              <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t("prospectLeads.ask.placeholder", "Describe the leads you want, e.g. CTOs of software companies in the United States with a phone number")} maxLength={1000} rows={3} data-testid="ask-question" />
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <label htmlFor="max-results" className="text-sm font-medium">{t("prospectLeads.search.howMany")}</label>
                  <Input id="max-results" type="number" min={1} max={status?.limits.maxResults ?? 500} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} placeholder={String(status?.limits.defaultMaxResults ?? 100)} className="w-32" />
                </div>
                <Button onClick={() => ask.mutate()} disabled={question.trim().length < 3 || !status?.available || ask.isPending || active} data-testid="ask-search">
                  {ask.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prospectLeads.ask.searching", "Searching...")}</> : <><Sparkles className="mr-2 h-4 w-4" />{t("prospectLeads.ask.search", "Search")}</>}
                </Button>
              </div>
              {ask.isPending && <p className="text-sm text-muted-foreground">{t("prospectLeads.ask.wait", "This can take up to a minute.")}</p>}
              {askAnswer?.explanation && <p className="text-sm" data-testid="ask-explanation"><span className="font-medium">{t("prospectLeads.find.explanation")}: </span>{askAnswer.explanation}</p>}
              {askAnswer?.truncated && <p className="text-sm text-muted-foreground">{t("prospectLeads.ask.truncated", "More leads match than are shown. Narrow the description to see others.")}</p>}
            </div>
          )}

          {!isAsk && (<>
          <div className="space-y-2">
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t("prospectLeads.find.placeholder")} maxLength={vocabQuery.data?.limits.promptMaxChars ?? 1000} rows={2} data-testid="lead-prompt" />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => suggest.mutate(false)} disabled={!prompt.trim() || suggest.isPending || !vocabQuery.data} data-testid="suggest-filters">
                {suggest.isPending && !suggest.variables ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prospectLeads.find.suggesting")}</> : <><Sparkles className="mr-2 h-4 w-4" />{t("prospectLeads.find.suggest")}</>}
              </Button>
              <Button onClick={() => suggest.mutate(true)} disabled={!prompt.trim() || suggest.isPending || !vocabQuery.data || !status?.available || active} data-testid="suggest-and-search">
                {suggest.isPending && suggest.variables ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prospectLeads.find.suggesting")}</> : <><Play className="mr-2 h-4 w-4" />{t("prospectLeads.find.suggestAndSearch")}</>}
              </Button>
            </div>
          </div>

          {parsed && (
            <div className="space-y-2" data-testid="parsed-prompt">
              {parsed.explanation && <p className="text-sm"><span className="font-medium">{t("prospectLeads.find.explanation")}: </span>{parsed.explanation}</p>}
              {(parsed.notApplied?.length ?? 0) > 0 && (
                <Alert data-testid="not-applied"><AlertTriangle className="h-4 w-4" /><AlertTitle>{t("prospectLeads.find.notApplied")}</AlertTitle>
                  <AlertDescription><ul className="list-disc pl-4">{parsed.notApplied.map((w, i) => <li key={i}>{w}</li>)}</ul></AlertDescription></Alert>
              )}
              {parsed.warnings.length > 0 && (
                <Alert><AlertTriangle className="h-4 w-4" /><AlertTitle>{t("prospectLeads.find.warnings")}</AlertTitle>
                  <AlertDescription><ul className="list-disc pl-4">{parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></AlertDescription></Alert>
              )}
            </div>
          )}

          <div className="border-t pt-4">
            {vocabQuery.isLoading ? <Skeleton className="h-24 w-full" /> : <LeadFilterForm fields={fields} values={formValues} onChange={setValues} />}
          </div>

          <div className="flex flex-wrap items-end gap-3 border-t pt-4">
            <div className="space-y-1.5">
              <label htmlFor="max-results" className="text-sm font-medium">{t("prospectLeads.search.howMany")}</label>
              <Input id="max-results" type="number" min={1} max={status?.limits.maxResults ?? 500} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} placeholder={String(status?.limits.defaultMaxResults ?? 100)} className="w-32" data-testid="max-results" />
            </div>
            <Button onClick={startSearch} disabled={!canSearch} data-testid="start-search">
              {start.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prospectLeads.search.starting")}</> : <><Play className="mr-2 h-4 w-4" />{t("prospectLeads.search.start")}</>}
            </Button>
            {!(hasCountry && hasNarrowing) && <p className="text-sm text-muted-foreground" data-testid="search-needs">{t("prospectLeads.search.needs")}</p>}
          </div>
          <p className="text-xs text-muted-foreground">{t("prospectLeads.search.timeHint")}</p>
          </>)}
        </CardContent>
      </Card>

      {job && (
        <Card data-testid="search-job">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-lg">{t("prospectLeads.search.resultsTitle")} {statusBadge(job.status)}</CardTitle>
              <div className="flex items-center gap-2">
                {job.results > 0 && <ColumnPicker choice={columns} groups={vocabQuery.data?.columnGroups ?? []} />}
                {job.results > 0 && <ExportButton request={{ scope: "search", jobId: job.id, columns: columns.keys }} label={t("prospectLeads.export.button")} testId="export-results" />}
                {active && (
                  <Button variant="outline" size="sm" onClick={() => stop.mutate()} disabled={stop.isPending} data-testid="stop-search"><Square className="mr-2 h-4 w-4" />{t("prospectLeads.search.stop")}</Button>
                )}
              </div>
            </div>
            <CardDescription>{job.summary}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {active && (
              <div className="space-y-2" data-testid="search-progress">
                <p className="flex items-center gap-2 text-sm font-medium"><Loader2 className="h-4 w-4 animate-spin" />{progressText(job)}</p>
                <Progress value={job.status === "queued" ? 0 : job.phasePercent} aria-label={progressText(job)} />
                <p className="text-sm text-muted-foreground">{t("prospectLeads.search.found", { count: job.results })}{job.datasetsDone > 0 && ` · ${t("prospectLeads.search.stats", { seen: job.matchesSeen, done: job.datasetsDone, total: job.datasetsTotal ?? "?" })}`}</p>
              </div>
            )}

            {job.status === "completed" && (
              <div className="space-y-2" data-testid="search-done">
                <p className="text-sm">{t("prospectLeads.search.found", { count: job.results })}. {doneText(job)}</p>
                {job.nextDataset && (
                  <Button variant="outline" size="sm" onClick={scanMore} disabled={start.isPending || !status?.available} data-testid="scan-more"><Play className="mr-2 h-4 w-4" />{t("prospectLeads.search.scanMore")}</Button>
                )}
              </div>
            )}
            {job.status === "cancelled" && <p className="text-sm text-muted-foreground" data-testid="search-cancelled">{t("prospectLeads.search.cancelled", { count: job.results })}</p>}
            {job.status === "failed" && (
              <Alert variant="destructive" data-testid="search-failed">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t("prospectLeads.search.failedTitle")}</AlertTitle>
                <AlertDescription>{job.errorMessage}{job.results > 0 && <span className="ml-1">{t("prospectLeads.search.failedKept", { count: job.results })}</span>}</AlertDescription>
              </Alert>
            )}
            {job.optedOut > 0 && <p className="text-xs text-muted-foreground" data-testid="opted-out-note">{t("prospectLeads.search.optedOut", { count: job.optedOut })}</p>}

            {selected.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3" data-testid="selection-bar">
                <span className="text-sm font-medium">{t("prospectLeads.leads.selected", { n: selected.size })}</span>
                {unsavedSelected > 0 && <Button size="sm" variant="outline" onClick={() => keep.mutate()} disabled={keep.isPending} data-testid="keep-results"><Save className="mr-2 h-4 w-4" />{t("prospectLeads.search.save")}</Button>}
                <Button size="sm" onClick={() => setShowCampaign(true)} data-testid="add-to-campaign"><UserPlus className="mr-2 h-4 w-4" />{t("prospectLeads.leads.addToCampaign")}</Button>
                <ExportButton request={{ scope: "ids", ids: [...selected.keys()], columns: columns.keys }} label={t("prospectLeads.export.selected", { count: selected.size })} testId="export-selected" />
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}><X className="mr-2 h-4 w-4" />{t("prospectLeads.leads.clearSelection")}</Button>
              </div>
            )}

            <LeadsTable rows={rows} columns={columns.selected} loading={jobQuery.isLoading} selected={selected} onToggleRow={toggleRow} onTogglePage={togglePage} emptyText={active ? t("prospectLeads.search.noneYet") : t("prospectLeads.search.none")} />

            {pagination && <LeadsPager page={page} pageSize={RESULTS_PER_PAGE} totalItems={pagination.totalItems} onPage={setPage} />}
          </CardContent>
        </Card>
      )}

      {(recentQuery.data?.data.length ?? 0) > 1 && (
        <div className="space-y-1" data-testid="recent-searches">
          <h3 className="text-sm font-medium">{t("prospectLeads.search.recent")}</h3>
          <ul className="divide-y rounded-lg border text-sm">
            {recentQuery.data!.data.slice(0, 5).map((j) => (
              <li key={j.id}>
                <button type="button" className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/50" onClick={() => { setJobId(j.id); setPage(1); setSelected(new Map()); }}>
                  <span className="truncate">{j.summary || j.country}</span>
                  <span className="flex items-center gap-2 text-muted-foreground">{t("prospectLeads.search.found", { count: j.results })}{statusBadge(j.status)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <AddToCampaignDialog open={showCampaign} onOpenChange={setShowCampaign} leads={selectedList} onDone={() => { setSelected(new Map()); queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search"] }); }} />
    </div>
  );
}
